import { useMutation } from "@tanstack/react-query";
import { easasSuccessAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { queryClient } from "services/HttpService";
import { T } from "services/lang/LanguageService";
import { useUserState } from "store/user-store";
import AlertsApi from "../AlertsApi";
import { AlertDTO } from "../model/alerts/alert-types";
import { defaultResponseHandler, IQueryOptions, QueryKey, useQueryWrapper } from "./api-hooks-common";

export const useGetCurrentAlerts = (options?: IQueryOptions<AlertDTO[]>) =>
  useQueryWrapper([QueryKey.ALERTS_CURRENT], () => AlertsApi.getCurrentAlerts(), null, {
    refetchInterval: 30000, // Poll every 30 seconds
    staleTime: 10000,
    ...options,
  });

export const useAcknowledgeAlert = () => {
  return useMutation({
    mutationFn: async (alertId: number) => {
      const res = await AlertsApi.acknowledgeAlert(alertId);
      return defaultResponseHandler(res);
    },
    onSuccess: (data, alertId) => {
      // Update cache optimistically
      queryClient.setQueryData([QueryKey.ALERTS_CURRENT], (oldData: AlertDTO[] | undefined) =>
        oldData?.map(alert => (alert.id === alertId ? { ...alert, isAcknowledged: true } : alert))
      );
      easasSuccessAlert(T("alert.acknowledged"));
    },
  });
};

export const useUnacknowledgeAlert = () => {
  return useMutation({
    mutationFn: async (alertId: number) => {
      const res = await AlertsApi.unacknowledgeAlert(alertId);
      return defaultResponseHandler(res);
    },
    onSuccess: (data, alertId) => {
      queryClient.setQueryData([QueryKey.ALERTS_CURRENT], (oldData: AlertDTO[] | undefined) =>
        oldData?.map(alert => (alert.id === alertId ? data : alert))
      );
    },
  });
};

export const useGetAlertHistory = (companyRef?: string, options?: IQueryOptions<AlertDTO[]>) => {
  const currentCompanyRef = useUserState(state => state.currentUser?.companyRef);
  const effectiveCompanyRef = companyRef || currentCompanyRef;

  return useQueryWrapper(
    [QueryKey.ALERTS_HISTORY, effectiveCompanyRef],
    () => AlertsApi.getAlertHistory(effectiveCompanyRef!),
    null,
    {
      enabled: !!effectiveCompanyRef,
      ...options,
    }
  );
};

export const useClearExpiredAlerts = () => {
  return useMutation({
    mutationFn: async () => {
      const res = await AlertsApi.clearExpiredAlerts();
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.ALERTS_HISTORY] });
      if (data) {
        easasSuccessAlert(`${data.clearedCount} expired alerts cleared`);
      }
    },
  });
};
